import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import PaymentMethodFormBillingAddress, {
  billingAddressSchema,
} from './payment-method-form-billing-address'
import { useMutation } from '@tanstack/react-query'
import { addPaymentMethodCardOrganizationOrganizationIdAddPaymentMethodCardPostMutation } from '@/generated/@tanstack/react-query.gen'
import { toast as customToast } from '@/components/custom-toast'
import { useUser } from '@/pages/enterprise/user-provider'
import { get } from 'lodash'
import { PaymentMethod } from '@stripe/stripe-js'

export const addPaymentCardSchema = z.object({
  cardholderName: z.string().min(2, 'Cardholder is required field.'),
  billingAddress: billingAddressSchema,
  authorizCheck: z
    .boolean()
    .refine(
      (val) => val === true,
      'You must agree to authorize Hydra Logic Inc. to charge.'
    ),
  acceptTerms: z
    .boolean()
    .refine(
      (val) => val === true,
      'You must agree to the Terms & Conditions to continue.'
    ),
})

const cardErrorMessage = 'Card info is required field.'

export type AddPaymentCardFormType = z.infer<typeof addPaymentCardSchema>

interface Props {
  onOpenChange: () => void
}

export default function PaymentMethodAddCard({ onOpenChange }: Props) {
  const { onUpdateSeats } = useUser()
  const stripe = useStripe()
  const elements = useElements()
  const cardValidationRef = useRef<boolean | null>(null)
  const [cardError, setCardError] = useState<string | null>(null)
  const [isFocused, setIsFocused] = useState(false)

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>()

  const { organizationId } = useUser()

  const form = useForm<AddPaymentCardFormType>({
    resolver: zodResolver(addPaymentCardSchema),
    defaultValues: {
      cardholderName: '',
      billingAddress: {
        address: '',
        state: '',
        city: '',
        zipCode: '',
      },
      authorizCheck: false,
      acceptTerms: false,
    },
  })

  const {
    mutate: createPaymentMethodMutate,
    isPending: isPendingCreatePaymentMethod,
  } = useMutation({
    mutationFn: async (formValues: AddPaymentCardFormType) => {
      if (!stripe || !elements) {
        throw new Error('Stripe not initialized')
      }
      const { paymentMethod, error: pmError } =
        // @ts-ignore
        await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement('card'),
          billing_details: {
            name: formValues.cardholderName,
            address: {
              line1: formValues.billingAddress.address,
              city: formValues.billingAddress.city,
              state: formValues.billingAddress.state,
              postal_code: formValues.billingAddress.zipCode,
            },
          },
        })

      if (pmError) {
        throw new Error('Failed to create payment method. Please try again.')
      }

      setPaymentMethod(paymentMethod)
      return paymentMethod
    },
    onError: (error) => {
      customToast.error({
        message: error.toString(),
      })
    },
  })

  const {
    mutate: addPaymentMethodMutate,
    isPending: isPendingAddPaymentMethod,
  } = useMutation({
    ...addPaymentMethodCardOrganizationOrganizationIdAddPaymentMethodCardPostMutation(
      {}
    ),
    onSuccess: async (data) => {
      onOpenChange()
      onUpdateSeats()
      if (get(data, 'status') === 'success') {
        return customToast.success({
          message: 'Payment method added successfully.',
        })
      }

      const clientSecret = get(data, 'clientSecret') as unknown as string
      const { error, paymentIntent } = await stripe!.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethod?.id, // Use the new payment method
        }
      )

      if (error) {
        return customToast.error({
          message: 'Failed to confirm card payment. Please try again.',
        })
      } else if (paymentIntent.status === 'succeeded') {
        return customToast.success({
          message: 'Payment method added successfully.',
        })
      }
    },
    onError: (error) => {
      if (error.status)
        customToast.error({
          message: error.toString(),
        })
    },
  })

  function onSubmit(data: AddPaymentCardFormType) {
    createPaymentMethodMutate(data, {
      onSuccess: (stripeResponse) => {
        // onOpenChange()
        addPaymentMethodMutate({
          path: { organization_id: organizationId },
          body: {
            payment_method_id: stripeResponse.id,
          },
        })
      },
    })
  }

  const validateCard = () => {
    return cardValidationRef.current ?? false
  }

  const isSubmitting = isPendingCreatePaymentMethod || isPendingAddPaymentMethod

  return (
    <div className="w-full py-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="gap-4 flex flex-col">
            <p
              className={cn(
                'text-lg font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                cardError ? 'text-destructive' : ''
              )}
            >
              Card Info
            </p>
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '18px',
                  },
                },
                hidePostalCode: true,
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onChange={(e) => {
                if (e.complete) {
                  cardValidationRef.current = true
                  setCardError(null)
                } else {
                  cardValidationRef.current = false
                  setCardError(cardErrorMessage)
                }
              }}
              className={cn(
                'w-full p-4 border focus:border-primary rounded-lg',
                isFocused ? 'border-primary border-[2px]' : 'border-input'
              )}
            />
            {cardError && (
              <p className={cn('text-[0.8rem] font-medium text-destructive')}>
                {cardError}
              </p>
            )}
          </div>

          <div className="space-y-4">
            <FormField
              control={form.control}
              name="cardholderName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cardholder Name</FormLabel>
                  <FormControl>
                    <Input className="h-14" placeholder="Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-4">
            <PaymentMethodFormBillingAddress form={form} />
          </div>

          <div className="space-y-4">
            <FormField
              control={form.control}
              name="authorizCheck"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex flex-row items-center gap-2">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="authorize-debit"
                      />
                      <label
                        htmlFor="authorize-debit"
                        className="cursor-pointer"
                      >
                        I authorize Hydra Logic Inc. to charge the card listed above
                        in accordance with the payment schedule outlined in my organization's Master Services Agreement.
                      </label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="acceptTerms"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex flex-row items-center gap-2">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="accept-terms"
                      />
                      <label htmlFor="accept-terms" className="cursor-pointer">
                        I have read and agree to{' '}
                        <a
                          href="https://hydrascore.com/terms-and-conditions"
                          className="text-primary hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Hydra Logic Inc. Terms & Conditions
                        </a>
                        .
                      </label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex gap-4">
            <Button
              disabled={isSubmitting}
              className="h-14 min-w-[113px]"
              type="submit"
              onClick={() => {
                if (!validateCard()) {
                  setCardError(cardErrorMessage)
                  return
                }
              }}
            >
              Add
            </Button>
            <Button
              disabled={isSubmitting}
              className="h-14 min-w-[113px]"
              type="button"
              variant="ghost"
              onClick={() => onOpenChange()}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
